import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  PauseIcon,
  PlayIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '~/components/atoms/IconButton';
import { Button } from '~/components/ui/button';
import { Slider } from '~/components/ui/slider';
import { Switch } from '~/components/ui/switch';
import { triggerExport, triggerExportAll } from '~/features/atlas/actions';
import { ClipsSection } from '~/features/clips/ClipsSection';
import { useActiveClip } from '~/features/clips/selectors';
import { FramesGrid } from '~/features/frames/FramesGrid';
import { useFrames } from '~/features/frames/useFrames';
import { useCleanedBitmap } from '~/features/source/SharedBitmapContext';
import { cn, DEFAULT_CLIP_FPS, MAX_CLIP_FPS, MIN_CLIP_FPS } from '~/lib/utils';
import { useStore } from '~/store';
import type { Frame, LayoutMode, MatchResult } from '~/types/pipeline';
import { AlignmentSection } from './AlignmentSection';
import { FramePreview } from './FramePreview';
import { LayoutSection } from './LayoutSection';
import { MatchConfidenceBadge } from './MatchConfidenceBadge';
import { getNormalizedPreviewCanvas } from './normalizedCanvas';
import { useAnimationLoop } from './useAnimationLoop';
import { useRowTick } from './useRowTick';

const THUMB_SMALL = 80;
const THUMB_LARGE = 224;
const THUMB_FPS = 8;
const PANEL_MIN_WIDTH = 280;
const PANEL_MAX_WIDTH = 560;
const PANEL_DEFAULT_WIDTH = 320;

type RowGroup = { key: number; frames: Frame[] };

export function clampPanelWidth(nextWidth: number): number {
  return Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, Math.round(nextWidth)));
}

export function buildRowGroups(frames: readonly Frame[], layoutMode: LayoutMode): RowGroup[] {
  if (frames.length === 0) return [];
  if (layoutMode.kind === 'sequence') {
    return [{ key: 0, frames: [...frames] }];
  }

  const rows = new Map<number, Array<{ col: number; frame: Frame }>>();

  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    if (!frame) continue;

    let row = 0;
    let col = i;
    if (layoutMode.kind === 'grid') {
      if (layoutMode.fill === 'row-major') {
        row = Math.floor(i / layoutMode.cols);
        col = i % layoutMode.cols;
      } else {
        row = i % layoutMode.rows;
        col = Math.floor(i / layoutMode.rows);
      }
    } else {
      const pos = layoutMode.positions[frame.index];
      row = pos?.row ?? 0;
      col = pos?.col ?? i;
    }

    const existing = rows.get(row);
    if (existing) existing.push({ col, frame });
    else rows.set(row, [{ col, frame }]);
  }

  return [...rows.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([key, rowFrames]) => ({
      key,
      frames: [...rowFrames]
        .sort((a, b) => a.col - b.col || a.frame.index - b.frame.index)
        .map((entry) => entry.frame),
    }));
}

export function AnimationPanel() {
  const dims = useStore((s) => s.source.dimensions);
  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);
  const dragStartRef = useRef<{ x: number; width: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!isResizing) return;

    const onMove = (event: MouseEvent) => {
      const drag = dragStartRef.current;
      if (!drag) return;
      const delta = drag.x - event.clientX;
      setPanelWidth(clampPanelWidth(drag.width + delta));
    };
    const onUp = () => {
      dragStartRef.current = null;
      setIsResizing(false);
    };

    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizing]);

  if (!dims) return null;
  return (
    <aside
      className="relative flex shrink-0 flex-col border-l bg-card"
      style={{ width: panelWidth }}
    >
      <div
        aria-hidden
        className={cn(
          'absolute inset-y-0 -left-1 z-10 flex w-2 cursor-col-resize items-center justify-center',
          isResizing ? 'bg-primary/10' : 'hover:bg-primary/5',
        )}
        onMouseDown={(event) => {
          event.preventDefault();
          dragStartRef.current = { x: event.clientX, width: panelWidth };
          setIsResizing(true);
        }}
      >
        <span className={cn('h-full w-px', isResizing ? 'bg-primary/60' : 'bg-border/70')} />
      </div>
      <div className="border-b px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Animation
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        <PanelBody />
      </div>
    </aside>
  );
}

function PanelBody() {
  const bitmap = useCleanedBitmap();
  const frames = useFrames();
  const layoutMode = useStore((s) => s.atlas.layoutMode);
  const framingMode = useStore((s) => s.pin.framingMode);
  const selectedRow = useStore((s) => s.animation.selectedRow);
  const onionStack = useStore((s) => s.animation.onionStack);
  const setOnionStack = useStore((s) => s.setOnionStack);
  const setSelectedRow = useStore((s) => s.setSelectedRow);

  const rowGroups = useMemo(() => buildRowGroups(frames, layoutMode), [frames, layoutMode]);
  const previousModeRef = useRef<{
    framingMode: 'uniform' | 'pin';
    layoutKind: LayoutMode['kind'];
  } | null>(null);

  useEffect(() => {
    if (selectedRow !== null && selectedRow >= rowGroups.length) {
      setSelectedRow(null);
    }
  }, [selectedRow, rowGroups.length, setSelectedRow]);

  useEffect(() => {
    const previous = previousModeRef.current;
    previousModeRef.current = { framingMode, layoutKind: layoutMode.kind };
    if (!previous) return;
    if (previous.framingMode === framingMode && previous.layoutKind === layoutMode.kind) return;
    if (selectedRow !== null) setSelectedRow(null);
  }, [framingMode, layoutMode.kind, selectedRow, setSelectedRow]);

  if (!bitmap || rowGroups.length === 0 || frames.length === 0) {
    return (
      <div className="space-y-2">
        <ClipsSection />
        <AlignmentSection />
        <LayoutSection />
        <p className="text-[11px] leading-snug text-muted-foreground">
          Define frames to see animation row previews and export controls.
        </p>
      </div>
    );
  }

  const selectedGroup = selectedRow !== null ? rowGroups[selectedRow] : null;
  const selectedFrames = selectedGroup?.frames ?? null;

  return (
    <div className="space-y-2">
      <ClipsSection />
      <AlignmentSection />
      <LayoutSection />
      {selectedRow !== null && selectedFrames && selectedFrames.length > 0 && selectedGroup ? (
        <div className="relative space-y-2 rounded border border-primary/40 bg-card p-2 ring-1 ring-primary/20">
          <button
            type="button"
            aria-label="Close row preview"
            onClick={() => setSelectedRow(null)}
            className="absolute right-1 top-1 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
          <BigRowPreview frames={selectedFrames} bitmap={bitmap} stack={onionStack} />
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Stack frames</span>
            <Switch
              aria-label="Stack all frames of the selected row"
              checked={onionStack}
              onCheckedChange={setOnionStack}
            />
          </div>
        </div>
      ) : (
        <RowThumbnailList rowGroups={rowGroups} bitmap={bitmap} />
      )}
      <ExportPanel />
      <FrameLayoutPanel />
    </div>
  );
}

function RowThumbnailList({ rowGroups, bitmap }: { rowGroups: RowGroup[]; bitmap: ImageBitmap }) {
  const selectedRow = useStore((s) => s.animation.selectedRow);
  const setSelectedRow = useStore((s) => s.setSelectedRow);
  const activeClip = useActiveClip();
  const matches = useStore((s) => s.referencePoint.results);
  const isReferencePoint = activeClip?.alignmentMode === 'reference-point';
  const matchByIndex = useMemo(() => {
    if (!isReferencePoint || !matches) return new Map<number, MatchResult>();
    return new Map(matches.map((m) => [m.frameIndex, m]));
  }, [isReferencePoint, matches]);
  const canvases = useMemo(
    () => rowGroups.map((g) => getNormalizedPreviewCanvas(g.frames)),
    [rowGroups],
  );
  const tick = useRowTick(THUMB_FPS);
  return (
    <>
      {rowGroups.map((group, listIndex) => {
        if (group.frames.length === 0) return null;
        const frame = group.frames[tick % group.frames.length];
        if (!frame) return null;
        const canvas = canvases[listIndex];
        if (!canvas) return null;
        const isSelected = selectedRow === listIndex;
        const match = matchByIndex.get(frame.index);
        return (
          <button
            key={group.key}
            type="button"
            onClick={() => setSelectedRow(isSelected ? null : listIndex)}
            className={cn(
              'relative flex w-full flex-col items-center gap-1 rounded border bg-card p-1 text-xs transition-colors',
              isSelected
                ? 'border-primary ring-2 ring-primary/40'
                : 'hover:border-muted-foreground/60',
            )}
          >
            <FramePreview frame={frame} bitmap={bitmap} size={THUMB_SMALL} canvas={canvas} />
            {match && <MatchConfidenceBadge match={match} />}
            <span className="text-muted-foreground">{group.frames.length} fr</span>
          </button>
        );
      })}
    </>
  );
}

function BigRowPreview({
  frames,
  bitmap,
  stack,
}: {
  frames: Frame[];
  bitmap: ImageBitmap;
  stack: boolean;
}) {
  const activeClip = useActiveClip();
  const fps = activeClip?.fps ?? DEFAULT_CLIP_FPS;
  useAnimationLoop(stack ? 0 : frames.length, fps);
  const isPlaying = useStore((s) => s.animation.isPlaying);
  const currentFrame = useStore((s) => s.animation.currentFrame);
  const setPlaying = useStore((s) => s.setPlaying);
  const setClipFps = useStore((s) => s.setClipFps);
  const stepFrame = useStore((s) => s.stepFrame);

  const canvas = useMemo(() => getNormalizedPreviewCanvas(frames), [frames]);
  const idx = currentFrame % frames.length;
  const frame = stack ? frames[0] : frames[idx];
  if (!frame) return null;

  return (
    <div className="space-y-2">
      <div className="flex flex-col items-center gap-1">
        <FramePreview
          frame={frame}
          bitmap={bitmap}
          size={THUMB_LARGE}
          canvas={canvas}
          {...(stack ? { stack: frames } : {})}
        />
        <span className="text-[11px] font-medium text-primary">
          {stack ? `${frames.length} frames` : `${idx + 1} / ${frames.length}`}
        </span>
      </div>
      {!stack && (
        <>
          <div className="flex items-center justify-center gap-1">
            <IconButton
              label="Previous frame"
              onClick={() => stepFrame(-1, frames.length)}
              disabled={frames.length < 2}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              label={isPlaying ? 'Pause' : 'Play'}
              onClick={() => setPlaying(!isPlaying)}
              disabled={frames.length < 2}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <IconButton
              label="Next frame"
              onClick={() => stepFrame(1, frames.length)}
              disabled={frames.length < 2}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>FPS</span>
            <Slider
              aria-label="Playback FPS"
              className="flex-1"
              value={[fps]}
              min={MIN_CLIP_FPS}
              max={MAX_CLIP_FPS}
              step={1}
              onValueChange={([v]) => {
                if (v == null || !activeClip) return;
                setClipFps(activeClip.id, v);
              }}
            />
            <span className="w-6 text-right tabular-nums">{fps}</span>
          </div>
        </>
      )}
    </div>
  );
}

function ExportPanel() {
  const exporting = useStore((s) => s.atlas.exporting);
  return (
    <section className="rounded border p-2">
      <div className="grid grid-cols-2 gap-1">
        <Button
          size="xs"
          variant="default"
          onClick={triggerExport}
          disabled={exporting}
          className="h-7"
        >
          <DownloadIcon />
          {exporting ? 'Exporting…' : 'Export ZIP'}
        </Button>
        <Button
          size="xs"
          variant="outline"
          onClick={triggerExportAll}
          disabled={exporting}
          className="h-7"
        >
          Export all clips
        </Button>
      </div>
    </section>
  );
}

function FrameLayoutPanel() {
  const layoutMode = useStore((s) => s.atlas.layoutMode);

  return (
    <section className="space-y-2 rounded border p-2">
      <div className="space-y-0.5">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          All frames
        </p>
        {layoutMode.kind === 'free' && (
          <p className="text-[11px] text-muted-foreground">
            Drag thumbnails to arrange free layout.
          </p>
        )}
      </div>
      <FramesGrid />
    </section>
  );
}
